import { useQuinceRouter } from '@src/modules/navigation'
import {
  Filter,
  FilterKeys,
  EFilterKeys,
  SelectedFilter,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { PRODUCT_FILTER_KEYS } from '@src/quince-site-common/src/constants/product'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { FilterOption, CheckboxOption, FilterItem, UpdateQueryParams } from './filterSection.interface'

// Constants
const FILTER_UPDATE_TYPES = {
  TOGGLE: 'toggle',
  OVERWRITE: 'overwrite',
} as const

/**
 * Maps filter labels to their corresponding filter keys
 */
// getFilterKeyFromLabel function removed

const DEPARTMENT_EXCLUDE_SLUG_LIST = [
  '/men',
  '/women',
  '/women/new-arrivals',
  '/men/new-arrivals',
  '/women/best-sellers',
  '/men/best-sellers',
  '/all/new-arrivals',
]

interface RadioFilterChangeParams {
  value: string
  options: CheckboxOption[]
  filter: FilterItem
  updateQuery: (params: UpdateQueryParams) => void
  serverFilters: Filter[]
  subCollectionCards?: SubCollectionCard[]
}

export function excludeDepartmentFilter(filters: Filter[], slug: string | undefined) {
  if (!slug) return filters
  return DEPARTMENT_EXCLUDE_SLUG_LIST.includes(slug)
    ? filters.filter(filter => filter.name !== (PRODUCT_FILTER_KEYS.departments as string))
    : filters
}

export function excludeFilterKeys(filters: Filter[], keysToExclude: readonly string[]) {
  if (!keysToExclude.length) return filters
  const excludeSet = new Set<string>(keysToExclude)
  return filters.filter(filter => !excludeSet.has(filter.name))
}

/**
 * Converts a string to title case
 */
export function toTitleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Creates checkbox options from filter values
 */
export function createCheckboxOptions(
  values: (string | FilterOption)[],
  selectedValues: string[],
  filterName: string,
): CheckboxOption[] {
  return values.map((value, index) => {
    const name = typeof value === 'string' ? value : value.name
    const displayName = typeof value === 'string' ? value : (value.displayName ?? value.name)
    return {
      id: `${filterName}-${displayName}-${index}`, // Include filterName in ID
      label: displayName,
      name: name,
      checked: selectedValues.includes(name),
    }
  })
}

/**
 * Handles navigation to a URL
 */
export function navigateTo(url: string, _isJumpLink = false, router: ReturnType<typeof useQuinceRouter>) {
  if (!url) return

  // Handle absolute URLs
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.location.href = url
    return
  }

  // Use a simple path format
  const formattedUrl = url.startsWith('/') ? url : `/${url}`
  router.push(formattedUrl)
}

/**
 * Calculates the total number of selected filters across all filter types
 */
export const calculateTotalSelectedFilters = (selectedFilters: any): number => {
  return Object.values(selectedFilters).reduce((total: number, filterValues) => {
    // Handle undefined, null, or non-array values
    if (!filterValues) return total
    if (!Array.isArray(filterValues)) return total
    return total + filterValues.length
  }, 0)
}

/**
 * Returns true if the filter should be visible.
 * A filter with only 1 value is kept if that value is currently selected;
 * otherwise it is hidden (same as the existing values.length > 1 rule).
 */
export function isFilterSelected(
  filter: { name: string },
  selectedFilters: SelectedFilter | Record<string, string[]>,
): boolean {
  if (filter.name === EFilterKeys.PRODUCT_TYPE || filter.name === EFilterKeys.SUBCOLLECTIONS) {
    return (
      (selectedFilters[EFilterKeys.PRODUCT_TYPE]?.length ?? 0) > 0 ||
      (selectedFilters[EFilterKeys.SUBCOLLECTIONS]?.length ?? 0) > 0
    )
  }
  return (selectedFilters[filter.name]?.length ?? 0) > 0
}

/**
 * Returns true when a selected value is present in the filter's current server-returned values.
 * Used to strip stale URL-state selections from counts and badges.
 */
const isValuePresentInFilter = (filter: FilterItem, value: string): boolean =>
  (filter.values ?? []).some(v => (typeof v === 'string' ? v === value : v.value === value || v.name === value))

/**
 * Checks if a filter has any selected values that are still present in the server response
 * @param filter The filter item to check (values reflect current server response)
 * @param selectedFiltersState Map of filter states
 * @returns Boolean indicating if the filter has server-valid selected values
 */
export const hasSelectedValues = (filter: FilterItem, selectedFiltersState: Record<string, string[]>) => {
  if (filter.name === EFilterKeys.PRODUCT_TYPE || filter.name === EFilterKeys.SUBCOLLECTIONS) {
    const productTypeState = selectedFiltersState[EFilterKeys.PRODUCT_TYPE] ?? []
    const subcollectionsState = selectedFiltersState[EFilterKeys.SUBCOLLECTIONS] ?? []
    return (
      productTypeState.some(v => isValuePresentInFilter(filter, v)) ||
      subcollectionsState.some(v => isValuePresentInFilter(filter, v))
    )
  }

  const state = selectedFiltersState[filter.name] ?? []
  return state.some(v => isValuePresentInFilter(filter, v))
}

/**
 * Gets the count of selected values that are still present in the server response.
 * Intersects URL state with filter.values so stale selections are not counted.
 * @param filter The filter item to check (values reflect current server response)
 * @param selectedFiltersState Map of filter states
 * @returns Number of server-valid selected values
 */
export const getSelectedCount = (filter: FilterItem, selectedFiltersState: Record<string, string[]>) => {
  if (filter.name === EFilterKeys.PRODUCT_TYPE || filter.name === EFilterKeys.SUBCOLLECTIONS) {
    const productTypeState = selectedFiltersState[EFilterKeys.PRODUCT_TYPE] ?? []
    const subcollectionsState = selectedFiltersState[EFilterKeys.SUBCOLLECTIONS] ?? []
    return Math.max(
      productTypeState.filter(v => isValuePresentInFilter(filter, v)).length,
      subcollectionsState.filter(v => isValuePresentInFilter(filter, v)).length,
    )
  }

  const state = selectedFiltersState[filter.name] ?? []
  return state.filter(v => isValuePresentInFilter(filter, v)).length
}

export const getDisplayNameForFilterValue = ({
  filters,
  filterName,
  filterValue,
}: {
  filters: Filter[]
  filterName: string
  filterValue: FilterKeys
}): string => {
  const filter = filters.find(f => f.name === filterName)
  if (!filter) return filterValue

  const filterValueOption = filter.values.find(value => {
    if (typeof value === 'string') {
      return value === filterValue
    }
    // As filterValueOption value will be in uppercase need to check value.name as well.
    return value.value === filterValue || value.name === filterValue
  })

  if (typeof filterValueOption === 'string') {
    return filterValueOption
  }

  return filterValueOption?.displayName ?? filterValueOption?.name ?? filterValue
}

/**
 * Returns true only when the server's current filter response includes the
 * given value. Used to hide chips for selections the server no longer surfaces.
 */
export function isFilterValueInServerSideFilters({
  filters,
  filterName,
  filterValue,
}: {
  filters: Filter[]
  filterName: string
  filterValue: string
}): boolean {
  const filter = filters.find(f => f.name === filterName)
  if (!filter) return false
  return filter.values.some(v => {
    if (typeof v === 'string') return v === filterValue
    return v.value === filterValue || v.name === filterValue
  })
}

/**
 * Normalizes text to kebab-case format for consistent comparison
 * @param text The text to normalize
 * @returns Normalized kebab-case string
 */
export function normalizeToKebabCase(text: string): string {
  return (
    text
      .toLowerCase()
      // Replace any non-alphanumeric characters with hyphens
      .replace(/[^a-z0-9]+/g, '-')
      // Remove all hyphens at start and end
      .replace(/^-+/, '')
      .replace(/-+$/, '')
  )
}

/**
 * Finds a matching filter option based on URL segment
 * @param options Array of filter options to search through
 * @param urlSegment The URL segment to match against
 * @returns Matching option or undefined
 */
export function findMatchingFilterOption(options: CheckboxOption[], urlSegment: string): CheckboxOption | undefined {
  const normalizedUrlSegment = normalizeToKebabCase(urlSegment)

  return options.find(opt => {
    const normalizedLabel = normalizeToKebabCase(opt.label)
    const normalizedId = normalizeToKebabCase(opt.id)

    return normalizedLabel === normalizedUrlSegment || normalizedId === normalizedUrlSegment
  })
}

/**
 * Gets the total selected filters count
 * @param selectedFiltersState Current selected filters state
 * @returns Total count of selected filters
 */
export function getTotalSelectedFiltersCount(selectedFiltersState: Record<string, string[]>): number {
  return Object.values(selectedFiltersState).reduce((total: number, filterValues) => {
    if (!filterValues) return total
    if (!Array.isArray(filterValues)) return total
    return total + filterValues.length
  }, 0)
}

interface SubCollectionCard {
  id: string
  title: string
  slug: string
}

/**
 * Gets the title from the current path by matching against subcollection cards
 */
export function getTitleFromPath(currentPath: string, subCollectionCards?: SubCollectionCard[]): string {
  if (!subCollectionCards?.length) return ''

  const matchingCard = subCollectionCards.find(card => card.slug === currentPath)
  return matchingCard?.title ?? ''
}

export function handleRadioFilterChange(params: RadioFilterChangeParams): void {
  const { value, options, filter, updateQuery } = params

  const selectedOption = options.find(opt => opt.id === value)

  if (selectedOption) {
    const filterName = filter.name === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filter.name
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_APPLIED, {
      filter_key: filterName,
      filter_value: selectedOption.label,
    })

    if (filterName === EFilterKeys.PRODUCT_TYPE) {
      const newSelectedFilters = SelectedFilter.initializeWithProductType(selectedOption.name || selectedOption.label)
      updateQuery({
        filter: {
          type: FILTER_UPDATE_TYPES.OVERWRITE,
          updatedFilters: newSelectedFilters,
        },
      })
    } else {
      updateQuery({
        filter: {
          type: FILTER_UPDATE_TYPES.TOGGLE,
          name: filter.name,
          value: selectedOption.name || selectedOption.label,
          label: filter.config.label,
        },
      })
    }
  }
}

export function getEnhancedSelectedFilterChips(
  baseSelectedFilterChips: { label: string; filterType: string; value: string }[],
): { label: string; filterType: string; value: string }[] {
  return [...baseSelectedFilterChips]
}

// getEnhancedClearAllFilters and getEnhancedHandleChipDismissByLabel removed as they are no longer specialized

/**
 * Formats a slug to be URL-friendly
 */
export function formatProductTypeSlug(slug: string): string {
  return slug
    .toLowerCase()
    .replace(/[&]/g, '') // Remove &
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '') // Remove any other special characters
    .replace(/^-+/, '') // Remove hyphens at start
    .replace(/-+$/, '') // Remove hyphens at end
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
}

/**
 * Transforms raw subcollection cards into a standardized format
 * @param cards Raw subcollection cards from props
 * @returns Transformed subcollection cards with standardized types
 */
export function transformSubCollectionCards(cards: any[] | undefined): SubCollectionCard[] | undefined {
  return cards?.map(card => ({
    id: String(card.id),
    title: card.title || '',
    slug: card.slug || '',
  }))
}

/**
 * Prepares radio filter data with common logic for both sidebar and mobile components
 */
export function prepareRadioFilterData(
  filter: FilterItem,
  selectedFiltersState: Record<string, string[]>,
): {
  filterName: string
  state: string[]
  currentValue: string
  options: CheckboxOption[]
} {
  const filterName = filter.name === EFilterKeys.PRODUCT_TYPE ? EFilterKeys.SUBCOLLECTIONS : filter.name
  const state = selectedFiltersState[filterName] ?? []
  const currentValue = state[0] || ''
  const options = createCheckboxOptions(filter.values, state, filterName)

  return {
    filterName,
    state,
    currentValue,
    options,
  }
}
